import { Link } from 'react-router';
import { cn } from '@/lib/utils';
import { formatDate, formatPercent } from '@/lib/formatters';
import { BookOpen } from 'lucide-react';
import { useCategoryLabel } from '@/api/categories';
import { isCompletedLate } from '@/lib/enrollment-late';
import type { Enrollment, EnrollmentStatus } from '@/types';

const STATUS_BADGES: Record<EnrollmentStatus, string> = {
  active: 'bg-amber-100 text-amber-700',
  completed: 'bg-green-100 text-green-700',
  expired: 'bg-gray-100 text-gray-600',
  cancelled: 'bg-red-100 text-red-700',
};

const STATUS_LABELS: Record<EnrollmentStatus, string> = {
  active: 'En Progreso',
  completed: 'Completado',
  expired: 'Vencido',
  cancelled: 'Cancelado',
};

const CATEGORY_COLORS: Record<string, string> = {
  induction: 'bg-blue-100 text-blue-700',
  periodic: 'bg-purple-100 text-purple-700',
  document_change: 'bg-amber-100 text-amber-700',
  general: 'bg-gray-100 text-gray-700',
  reinforcement: 'bg-teal-100 text-teal-700',
  technical: 'bg-cyan-100 text-cyan-700',
  soft_skills: 'bg-pink-100 text-pink-700',
  compliance: 'bg-indigo-100 text-indigo-700',
  mandatory: 'bg-red-100 text-red-700',
};

const CATEGORY_BORDER: Record<string, string> = {
  induction: 'border-t-blue-500',
  periodic: 'border-t-purple-500',
  document_change: 'border-t-amber-500',
  general: 'border-t-gray-400',
  reinforcement: 'border-t-teal-500',
  technical: 'border-t-cyan-500',
  soft_skills: 'border-t-pink-500',
  compliance: 'border-t-indigo-500',
  mandatory: 'border-t-red-500',
};

export function EnrollmentCard({ enrollment }: { enrollment: Enrollment }) {
  const categoryLabel = useCategoryLabel('course_category');
  return (
    <div
      className={cn(
        'flex flex-col rounded-xl border border-border border-t-4 bg-card transition-shadow hover:shadow-md',
        CATEGORY_BORDER[enrollment.course.category] ?? CATEGORY_BORDER.general,
      )}
    >
      {/* Thumbnail */}
      {enrollment.course.thumbnail_url ? (
        <div className="aspect-video w-full overflow-hidden bg-muted">
          <img
            src={enrollment.course.thumbnail_url}
            alt={enrollment.course.title}
            className="h-full w-full object-cover"
          />
        </div>
      ) : (
        <div className="flex aspect-video w-full items-center justify-center bg-muted/50">
          <BookOpen className="h-10 w-10 text-muted-foreground/30" />
        </div>
      )}

      <div className="flex flex-1 flex-col p-5">
        <h3 className="line-clamp-2 text-base font-semibold text-card-foreground">
          {enrollment.course.title}
        </h3>

        <div className="mt-2 flex flex-wrap gap-1.5">
          <span
            className={cn(
              'rounded-full px-2 py-0.5 text-xs font-medium',
              CATEGORY_COLORS[enrollment.course.category] ?? CATEGORY_COLORS.general,
            )}
          >
            {categoryLabel(enrollment.course.category)}
          </span>
          <span
            className={cn(
              'rounded-full px-2 py-0.5 text-xs font-medium',
              STATUS_BADGES[enrollment.status],
            )}
          >
            {STATUS_LABELS[enrollment.status]}
          </span>
          {isCompletedLate(enrollment) && (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
              Tarde
            </span>
          )}
        </div>

        <div className="mt-4">
          <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
            <span>Progreso</span>
            <span className="font-medium">{formatPercent(enrollment.progress_percent)}</span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
            <div
              className={cn(
                'h-full rounded-full transition-all',
                enrollment.status === 'completed' ? 'bg-green-500' : 'bg-primary',
              )}
              style={{ width: `${enrollment.progress_percent}%` }}
            />
          </div>
        </div>

        <div className="mt-3 space-y-0.5 text-xs text-muted-foreground">
          <p>Inscrito: {formatDate(enrollment.enrolled_at)}</p>
          {enrollment.due_date && <p>Vence: {formatDate(enrollment.due_date)}</p>}
        </div>
      </div>

      <div className="border-t border-border px-5 py-3">
        {enrollment.status === 'cancelled' ? (
          <span className="inline-flex w-full items-center justify-center rounded-lg bg-muted px-4 py-2 text-sm font-medium text-muted-foreground">
            Inscripción cancelada
          </span>
        ) : (
          <div className="flex gap-2">
            <Link
              to={`/courses/${enrollment.id}`}
              className="inline-flex flex-1 items-center justify-center rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
            >
              Ver Curso
            </Link>
            {enrollment.status === 'completed' && (
              <Link
                to="/certificates"
                className="inline-flex flex-1 items-center justify-center rounded-lg border border-border bg-card px-4 py-2 text-sm font-medium text-card-foreground transition-colors hover:bg-accent"
              >
                Ver Certificado
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
